import type { TestMode } from "../shared/api/types";
import { currentRoleLabel } from "../shared/crew/crewLabels";

/** Words and select values for the test-mode switch and strip (TestMode.tsx). */

export const OFF = "off";
export const USER = "user";
export const OTHER_PROJECT = "other-project";
export const VIEWED_USER = "viewed-user";

/** The strip's sentence (without the Turn off button). Null when test mode is off. On production (0.10.58) it opens with
 * "Production —" and says the changes are real; wherever an idle limit applies it says when test mode ends. */
/** `roleWords` shows a role key: the crew picker label (Revision 104); by default from the table the app last loaded. */
export function testModeSentence(
  testMode: TestMode | undefined,
  projectKey: string,
  rolesHere: readonly string[],
  roleWords: (role: string) => string = currentRoleLabel,
): string | null {
  if (!testMode?.active) return null;
  const production = testMode.production === true;
  const lead = production ? "Production — you are" : "Test mode: you are";
  let sentence: string;
  if (testMode.kind === "user") {
    const name = testMode.asUser?.displayName ?? "another user";
    const roles = rolesHere.length > 0 ? rolesHere.map((role) => roleWords(role)).join(", ") : "no role";
    sentence = projectKey ? `${lead} viewing as ${name} (${roles} on ${projectKey})` : `${lead} viewing as ${name}`;
  } else {
    const role = roleWords(testMode.role ?? "");
    const where = testMode.projectKey ?? "";
    sentence =
      where === projectKey || !projectKey
        ? `${lead} acting as ${role} on ${where}`
        : `${lead} acting as ${role} on ${where} only. On ${projectKey} you have your own roles`;
  }
  if (production) sentence += ". Test mode: changes are real and recorded under your own name";
  if (testMode.idleMinutes) sentence += `. Ends after ${testMode.idleMinutes} minutes without activity`;
  return sentence;
}

/** The select's current value for the header control. */
export function testModeValue(testMode: TestMode | undefined, projectKey: string): string {
  if (!testMode?.active) return OFF;
  if (testMode.kind === "user") return VIEWED_USER;
  return testMode.projectKey === projectKey ? `role:${testMode.role ?? ""}` : OTHER_PROJECT;
}

/**
 * The project "Role on <project>" applies to. Normally the header's current project. While viewing as a user the header's
 * project list is the viewed person's, so the switch chooses from `TestMode.projects` instead, the real admin's list
 * (test round R7, R7-D01): the admin's own pick if it is still listed, else the current project if listed, else the first.
 */
export function roleProjectFor(testMode: TestMode | undefined, projectKey: string, picked: string | null): string {
  if (!testMode?.active || testMode.kind !== "user") return projectKey;
  const keys = testMode.projects.map((project) => project.key);
  if (keys.length === 0) return projectKey;
  if (picked && keys.includes(picked)) return picked;
  if (keys.includes(projectKey)) return projectKey;
  return keys[0] ?? projectKey;
}
